"use client";

import { useRef } from "react";

import Image from "next/image";

import { colors } from "@/styles/design-tokens";

/**
 * Wide Search Bar Component
 * Figma: Wide Search bar (Node: 482:27308)
 *
 * Reusable 900px × 60px search bar
 * Used in: Hero Search, Brand Discovery, etc.
 *
 * Dimensions: 900px × 60px
 * Typography: placeholderLg (16px, -0.7125px tracking)
 * Border: Bottom only, rgba(0,0,0,0.1)
 * Icon: 20×20px search icon (gray)
 */

interface WideSearchBarProps {
  value?: string;
  onChange?: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  onSubmit?: () => void;
  placeholder?: string;
  className?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  isFocused?: boolean;
  submitLabel?: string;
  submitAriaLabel?: string;
}

// Animated icon that collapses/expands with transitions
function AnimatedIcon({
  show,
  src,
  size,
  marginRight,
}: {
  show: boolean;
  src: string;
  size: number;
  marginRight?: number;
}) {
  return (
    <span
      className="flex-shrink-0 overflow-hidden transition-all duration-200"
      style={{
        width: show ? size : 0,
        height: size,
        opacity: show ? 1 : 0,
        marginRight: show && marginRight ? marginRight : 0,
      }}
    >
      <Image src={src} alt="" width={size} height={size} />
    </span>
  );
}

export function WideSearchBar({
  value = "",
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  onSubmit,
  placeholder = "What are you looking for?",
  className = "",
  inputRef: externalRef,
  isFocused = false,
  submitLabel = "Search",
  submitAriaLabel,
}: WideSearchBarProps) {
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = externalRef || internalRef;

  const hasValue = value && value.trim().length > 0;
  const showSearchButton = isFocused || hasValue;

  return (
    <div
      className={`relative ${className}`}
      style={{
        width: "100%",
        maxWidth: "900px",
        height: "60px",
        borderBottom: `1px solid ${colors.divider.divider1}`,
        borderRadius: "0",
      }}
    >
      {/* Text Input Container */}
      <div className="flex h-full items-center overflow-hidden pr-28">
        {/* Search Icon - appears to left when typing */}
        <AnimatedIcon show={!!hasValue} src="/icons/search-white.svg" size={20} marginRight={8} />

        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          data-testid="hero-search-input"
          className={`text-input-lg flex-1 border-0 bg-transparent outline-none transition-all duration-200 ${value ? "text-ink" : "text-ink-faint"}`}
          style={{
            borderRadius: "0",
            WebkitAppearance: "none",
            padding: "0",
            textOverflow: "ellipsis",
          }}
        />
      </div>

      {/* Search Button Container */}
      <button
        type="button"
        onClick={() => {
          if (value?.trim()) {
            onSubmit?.();
          }
        }}
        className="absolute flex cursor-pointer items-center justify-end gap-2 border-none bg-transparent"
        style={{ right: 0, top: 20, height: 20, paddingLeft: 8 }}
        aria-label={submitAriaLabel ?? submitLabel}
      >
        {/* "SEARCH →" button - shows when focused/has value */}
        <span
          className="text-overline text-ink flex items-center gap-2 overflow-hidden transition-all duration-200 whitespace-nowrap"
          style={{
            opacity: showSearchButton ? 1 : 0,
            width: "auto",
            maxWidth: showSearchButton ? "220px" : 0,
          }}
        >
          {submitLabel}
          <Image src="/icons/arrow-right.svg" alt="" width={14} height={14} />
        </span>

        {/* Search Icon - shows on right only when idle */}
        <AnimatedIcon show={!showSearchButton} src="/icons/search-white.svg" size={20} />
      </button>
    </div>
  );
}
